import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { UserPlus, Copy, CheckCircle2, Mail, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const ROLES = [
  { value: "landlord", label: "Investor", hint: "A paying portfolio owner — manages properties, loans, tenants." },
  { value: "tenant", label: "Tenant", hint: "Rents a unit — views lease, pays rent online, files maintenance tickets." },
  { value: "admin", label: "Platform Admin", hint: "Elevated role — full access to every account and the revenue console." },
];

const emptyForm = { role: "landlord", email: "", full_name: "", company: "", phone: "", landlord_id: "" };

/**
 * Superadmin-only dialog to create a new account (investor / tenant / admin).
 * Backend endpoint: POST /api/admin/accounts — generates a temp password,
 * persists the user, and attempts to send a Resend welcome email. The temp
 * password is always returned in the JSON so we can show it in a success
 * modal (fallback when Resend is in MOCK mode).
 */
export function CreateAccountDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [investors, setInvestors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { user, temp_password, email_status }
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm);
    setResult(null);
    setCopied(false);
    // Lazy-load investors list (needed only for role=tenant)
    api.get("/admin/landlords")
      .then(({ data }) => setInvestors(Array.isArray(data) ? data : []))
      .catch(() => { /* silently ignore — dropdown will show empty state */ });
  }, [open]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.email.trim() || !form.full_name.trim()) {
      toast.error("Email and full name are required");
      return;
    }
    if (form.role === "tenant" && !form.landlord_id) {
      toast.error("Choose an investor to assign this tenant to");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        role: form.role,
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        company: form.company.trim(),
        phone: form.phone.trim(),
      };
      if (form.role === "tenant") payload.landlord_id = form.landlord_id;
      const { data } = await api.post("/admin/accounts", payload);
      setResult(data);
      onCreated?.(data.user);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to create account");
    } finally {
      setSaving(false);
    }
  };

  const copyPassword = async () => {
    if (!result?.temp_password) return;
    try {
      await navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      toast.success("Password copied");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed — please select the password manually");
    }
  };

  const close = () => {
    setResult(null);
    setCopied(false);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-lg" data-testid="create-account-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-slate-500" />
            {result ? "Account created" : "Create new account"}
          </DialogTitle>
        </DialogHeader>

        {!result && (
          <form onSubmit={submit} className="space-y-4">
            {/* Role picker */}
            <div>
              <div className="overline text-slate-500 mb-2">Role</div>
              <div className="grid grid-cols-3 gap-2" data-testid="create-account-role-group">
                {ROLES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => update({ role: r.value, landlord_id: "" })}
                    className={`px-3 py-2 rounded-sm border text-sm font-mono uppercase tracking-widest transition ${form.role === r.value ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"}`}
                    data-testid={`create-account-role-${r.value}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <div className="text-xs text-slate-500 mt-2">
                {ROLES.find((r) => r.value === form.role)?.hint}
              </div>
            </div>

            {form.role === "tenant" && (
              <Field label="Assign to investor" required>
                <select
                  required
                  className="input-clean w-full"
                  value={form.landlord_id}
                  onChange={(e) => update({ landlord_id: e.target.value })}
                  data-testid="create-account-landlord-select"
                >
                  <option value="">Select investor</option>
                  {investors.map((ll) => (
                    <option key={ll.id} value={ll.id}>
                      {ll.full_name}{ll.company ? ` — ${ll.company}` : ""} ({ll.email})
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Full name" required>
                <input
                  required
                  className="input-clean w-full"
                  value={form.full_name}
                  onChange={(e) => update({ full_name: e.target.value })}
                  data-testid="create-account-name-input"
                />
              </Field>
              <Field label="Email" required>
                <input
                  required
                  type="email"
                  className="input-clean w-full"
                  value={form.email}
                  onChange={(e) => update({ email: e.target.value })}
                  data-testid="create-account-email-input"
                />
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Company">
                <input
                  className="input-clean w-full"
                  value={form.company}
                  onChange={(e) => update({ company: e.target.value })}
                  data-testid="create-account-company-input"
                />
              </Field>
              <Field label="Phone">
                <input
                  className="input-clean w-full"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={(e) => update({ phone: e.target.value })}
                  data-testid="create-account-phone-input"
                />
              </Field>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs text-slate-600 flex items-start gap-2">
              <Mail className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
              <span>We'll generate a temporary password and email a welcome link. You'll also see the password here so you can share it directly if email delivery is pending.</span>
            </div>

            <DialogFooter className="gap-2">
              <button type="button" className="btn-outline-dark" onClick={close} disabled={saving} data-testid="create-account-cancel">Cancel</button>
              <button type="submit" className="btn-primary-dark" disabled={saving} data-testid="create-account-submit">
                {saving ? "Creating…" : "Create account"}
              </button>
            </DialogFooter>
          </form>
        )}

        {result && (
          <div className="space-y-4" data-testid="create-account-success">
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="w-5 h-5" />
              <div className="text-sm font-semibold text-slate-900">{result.user.full_name} · {roleLabelFor(result.user.role)}</div>
            </div>

            <div className="rounded-sm border border-slate-200 bg-slate-50 overflow-hidden">
              <Row label="Email" value={result.user.email} mono />
              <Row
                label="Temporary password"
                value={result.temp_password}
                mono
                bold
                action={(
                  <button
                    type="button"
                    onClick={copyPassword}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-mono uppercase tracking-widest text-slate-700 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200 transition"
                    data-testid="create-account-copy-password"
                  >
                    {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                )}
              />
            </div>

            {result.email_status === "sent" ? (
              <div className="flex items-start gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2">
                <Mail className="w-3.5 h-3.5 text-emerald-600 mt-0.5" />
                <span className="text-emerald-900">Welcome email sent to {result.user.email}</span>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5" />
                <span className="text-amber-900">
                  Welcome email could not be delivered ({result.email_reason || result.email_status || "mock mode"}).
                  Share the temporary password with the user directly.
                </span>
              </div>
            )}

            <DialogFooter>
              <button type="button" className="btn-primary-dark" onClick={close} data-testid="create-account-done">Done</button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function roleLabelFor(role) {
  return ({ landlord: "Investor", tenant: "Tenant", admin: "Platform Admin" })[role] || role;
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-500 mb-1">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</div>
      {children}
    </label>
  );
}

function Row({ label, value, mono, bold, action }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 border-b last:border-b-0 border-slate-200">
      <div className="overline text-slate-500">{label}</div>
      <div className="flex items-center gap-2 min-w-0">
        <div className={`truncate ${mono ? "font-mono" : ""} ${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>{value}</div>
        {action}
      </div>
    </div>
  );
}
